import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy,Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule, Sort, SortDirection } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';

import { AdminComponentBase, PageSimpleContentComponent } from 'app/layout/content';
import { DisplayCodeDialogComponent, DisplayCodeDialogData } from 'app/pages/store-support/order/display-code-dialog.component';
import { SortOrder, StoreWebhookLog } from 'app/shared/graph';
import { FromNowPipe } from 'app/shared/utilities/from-now.pipe';

import { StoreWebhookLogDetailDialogComponent, StoreWebhookLogDetailDialogData } from './store-webhook-log-detail-dialog.component';
import { StoreWebhookLogsService } from './store-webhook-logs.service';

type FilterType = {
    dateOrdering: SortDirection;
    startDate?: Date;
    endDate?: Date;
}

@Component({
    selector: 'admin-webhook-logs-page',
    imports: [
        PageSimpleContentComponent,
        MatTableModule,
        DatePipe,
        FromNowPipe,
        MatFormFieldModule,
        MatDatepickerModule,
        MatIconButton,
        MatIcon,
        FormsModule,
        MatSelectModule,
        MatPaginatorModule,
        MatSortModule,
        MatButtonModule,
        MatCardModule,
        MatInput
    ],
    templateUrl: './store-webhook-logs-page.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './store-webhook-logs-page.component.scss'
})

export class StoreWebhookLogsPageComponent extends AdminComponentBase implements OnInit, OnDestroy {
    private readonly route = inject(ActivatedRoute);
    private readonly storeWebhookLogsService = inject(StoreWebhookLogsService);
    private readonly dialog = inject(MatDialog);

    storeId: string;
    webhookLogs: StoreWebhookLog[];
    displayedColumns = ['url', 'status', 'sentDateUtc', 'actions'];
    webhookLogEnabled?: boolean = undefined;

    hasNextPage = false;
    resultPerPage = 50;
    page = 0;
    filter: FilterType = {
        dateOrdering: 'desc'
    };
    searchText?: string

    ngOnInit(): void {
        this.registerSubscription(this.route.params.subscribe(params => {
            this.storeId = params.storeId;
            this.updateWebhookLogs();
            this.webhookLogEnabled = undefined;
            this.registerSubscription(this.storeWebhookLogsService.getStoreWebhookLogEnabled(this.storeId).subscribe(webhookLogEnabled => {
                this.webhookLogEnabled = webhookLogEnabled;
            }))
        }));
    }

    ngOnDestroy() {
        this.unsubscribeSubscriptions();
    }

    updateWebhookLogs() {
        this.subscribeWithGenericLoadingErrorHandling(this.storeWebhookLogsService.getStoreWebhookLogs(
            this.storeId,
            this.resultPerPage + 1,
            this.page * this.resultPerPage,
            this.filter.dateOrdering === 'asc' ? SortOrder.Asc : SortOrder.Desc,
            this.filter.startDate,
            this.filter.endDate ? new Date(this.filter.endDate.getTime() + 24 * 3600 * 1000) : undefined
        ), webhookLogs => {
            this.hasNextPage = webhookLogs.length > this.resultPerPage;
            this.webhookLogs = webhookLogs;
        });

        // this.saveFilter();
    }

    clearDate() {
        this.filter.startDate = undefined;
        this.filter.endDate = undefined;
        this.updateFilter();
    }

    updateFilter() {
        this.page = 0;
        this.updateWebhookLogs();
    }

    loadPage(page: number) {
        this.page = page;
        this.updateWebhookLogs();
    }

    updateSort($event: Sort) {
        this.filter.dateOrdering = $event.direction;
        this.updateFilter();
    }

    $storeWebhookLog(log: any): StoreWebhookLog {
        return log as StoreWebhookLog;
    }

    reload() {
        this.updateWebhookLogs();
    }

    enableWebhookLogging() {
        this.subscribeWithGenericSavingErrorHandling(this.storeWebhookLogsService.updateStoreConfig({
            storeId: this.storeId,
            webhookLogEnabled: true
        }), () => this.webhookLogEnabled = true);
    }

    showLogDetails(webhookLog: StoreWebhookLog) {
        this.dialog.open<StoreWebhookLogDetailDialogComponent, StoreWebhookLogDetailDialogData>(StoreWebhookLogDetailDialogComponent, {
            width: '80vw',
            height: '80vh',
            data: { webhookLog: webhookLog }
        });
    }

    showLog(webhookLog: StoreWebhookLog) {
        this.dialog.open<DisplayCodeDialogComponent, DisplayCodeDialogData>(DisplayCodeDialogComponent, {
            width: '80vw',
            height: '80vh',
            data: { text: JSON.stringify(webhookLog, null, 4), language: 'json' }
        });
    }

    isMatchingSearch(webhookLog: StoreWebhookLog): boolean {
        if (!this.searchText)
            return false;
        if (webhookLog.requestBody?.includes(this.searchText))
            return true;
        if (webhookLog.responseBody?.includes(this.searchText))
            return true;
        for (let responseHeader of webhookLog.responseHeaders) {
            if (responseHeader.value?.includes(this.searchText))
                return true;
        }
        for (let requestHeaders of webhookLog.requestHeaders) {
            if (requestHeaders.value?.includes(this.searchText))
                return true;
        }
        return false;
    }
}
